import { RADIO_RECOVERY } from '@bedrock-rdk/media-engine';
import { HTML5Engine } from '@bedrock-rdk/media-engine/src/engines/html5/html5.engine';
import { PlaybackController } from '../player/playback.controller';
import { playerDispatcher } from '../player/player.dispatcher';

// NOTE: only the radio flow needs shaka, so it is loaded on demand. A static import here puts
// shaka-player back into the entry chunk for every customer whose routes reach this module.
const loadShakaEngine = () =>
  import(
    /* webpackChunkName: "engine.shaka" */
    '@bedrock-rdk/media-engine/src/engines/shaka/shaka.engine'
  ).then(module => module.ShakaEngine);

const isRadioPlayer = id => !id.startsWith('audio');

const makeAudioElement = id => {
  const element = document.createElement('audio');
  element.setAttribute('id', id);
  element.setAttribute('autoplay', true);
  // NOTE: despite it may fix CORS issues in a normal browser, on a contrary, it creates CORS issues on idempotent GET requests on STBs
  // element.setAttribute('crossorigin', 'anonymous');
  document.body.appendChild(element);

  return element;
};

// NOTE: the audio playback outlives the screens, so it is shared by the audio page and the mini player
const playback = new PlaybackController({
  loadEngine: ({ id }) => (isRadioPlayer(id) ? loadShakaEngine() : HTML5Engine),
  createSurface: ({ id }) => {
    const element = makeAudioElement(id);

    return { element, container: element };
  },
  getOptions: ({ id }) => ({
    dispatcher: playerDispatcher,
    ...(isRadioPlayer(id) ? { recovery: RADIO_RECOVERY } : {}),
  }),
});

export const getAudioPlayerInstance = () => playback.engine;

/**
 * @returns {Promise} resolves with the audio player once it is usable, or with null when its loading was cancelled
 */
export const whenAudioPlayerReady = () => playback.whenReady();

export const destroyAudioPlayer = () => {
  playback.release();

  return true;
};

/**
 * Creates the audio player node and its engine, unless they are already there
 *
 * @param {string} id identifier of the audio element to create, a radio one unless it starts with "audio"
 * @returns {Promise} resolves with the audio player once it is usable, or with null when its loading was cancelled
 */
export const initAudioPlayer = (id = 'audio-player') => {
  if (playback.params && playback.params.id === id) {
    // eslint-disable-next-line no-console
    console.log(`[AudioAdapter] Audio player ${id} already exists, no need to init`);

    return whenAudioPlayerReady();
  }

  playback.release();

  return playback.load({ id });
};
